"use client";

import React, { useEffect, useState, useRef, useCallback } from "react";
import Logo from "@/app/components/Logo";
import PageContainer from "@/app/components/ui-components/PageContainer";
import DashboardCard from "@/app/components/shared/DashboardCard";
import AppVersionFooter from "@/app/components/AppVersionFooter";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import { FaLinkedinIn } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { useRouter, useSearchParams } from "next/navigation";
import { signIn, useSession } from "next-auth/react";
import { track } from "@/lib/analytics/track";
import { EVENTS } from "@/lib/analytics/events";
import {
  Typography,
  Box,
  CircularProgress,
  Chip,
  Button,
  TextField,
  IconButton,
  useMediaQuery,
  Fade,
  useTheme,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MailOutlineIcon from "@mui/icons-material/MailOutlined";
import Grid from "@mui/material/Grid";
import EmailAuthSection, {
  getInboxUrl,
} from "@/app/components/auth/EmailAuthSection";
import useLastLoginProvider from "@/lib/hooks/useLastLoginProvider";

// Constants
const ERROR_MESSAGES = {
  AccountBlocked:
    "Your account has been suspended. If you think this was done in error, please reach out to us at support@meex.co and we'll look into it.",
  AccessDenied:
    "No problem, LinkedIn sign-in was cancelled. You can try again whenever you're ready.",
  OAuthCallback:
    "No problem, LinkedIn sign-in was cancelled. You can try again whenever you're ready.",
  OAuthSignin:
    "No problem, LinkedIn sign-in was cancelled. You can try again whenever you're ready.",
  Callback:
    "No problem, LinkedIn sign-in was cancelled. You can try again whenever you're ready.",
  SignInError:
    "No problem, LinkedIn sign-in was cancelled. You can try again whenever you're ready.",
  access_denied:
    "No problem, LinkedIn sign-in was cancelled. You can try again whenever you're ready.",
  TooManyRequests:
    "Too many sign-in attempts. Please wait a few minutes and try again.",
  CredentialsSignin:
    "This sign-in link is invalid or has already been used. Please request a new one.",
  AccountLinkRequired:
    "We found a possible existing account. Sign in using your original method, then verify and connect your LinkedIn account in settings. If you cannot access it, contact support@meex.co.",
  AccountIdentityConflict:
    "We found conflicting account records and have stopped this sign-in to protect your meetings. Sign in using your original method or contact support@meex.co to resolve the accounts.",
  FirestoreError:
    "We could not check your account right now. Please try again. No new account was created.",
  default: "Sign-in wasn't completed. You can try again anytime.",
};

const ERROR_TITLES = {
  AccountBlocked: "Account suspended",
  AccountLinkRequired: "Verify your existing account",
  AccountIdentityConflict: "Account needs attention",
  FirestoreError: "Account check unavailable",
  AccessDenied: "Sign-in cancelled",
  OAuthCallback: "Sign-in cancelled",
  OAuthSignin: "Sign-in cancelled",
  Callback: "Sign-in cancelled",
  SignInError: "Sign-in cancelled",
  access_denied: "Sign-in cancelled",
  default: "Sign-in not completed",
};

const formatStatNumber = (num) => {
  if (!num || num <= 0) return null;
  // Until we cross 10,000 real portfolios, show a friendlier "10,000+" badge.
  if (num < 10000) return "10,000+";
  return `${(Math.floor(num / 10000) * 10000).toLocaleString()}+`;
};

// Components
const AuthErrorMessage = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const error = searchParams?.get("error");
  const emailConfirmed = searchParams?.get("emailConfirmed");
  const emailLinkError = searchParams?.get("emailLinkError");

  // email link confirmation / error (from confirm-signin-email route)
  const [emailDialogOpen, setEmailDialogOpen] = useState(
    emailConfirmed === "1" || !!emailLinkError,
  );
  const [authErrorOpen, setAuthErrorOpen] = useState(Boolean(error));

  useEffect(() => {
    setAuthErrorOpen(Boolean(error));
  }, [error]);

  if (emailDialogOpen) {
    const isSuccess = emailConfirmed === "1";
    return (
      <ConfirmDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        onConfirm={() => setEmailDialogOpen(false)}
        title={isSuccess ? "Email confirmed" : "Email link error"}
        body={
          isSuccess
            ? "Your sign-in email has been confirmed. Sign in to continue to your settings."
            : decodeURIComponent(emailLinkError || "Something went wrong.")
        }
        confirmButtonText="Okay"
        showIgnoreButton={false}
        showCloseIcon={false}
        modalStyle="toast"
        confirmButtonVariant="text"
      />
    );
  }

  // standard NextAuth sign-in errors (AccountBlocked, TooManyRequests, etc.)
  if (!error) return null;
  const errorMessage = ERROR_MESSAGES[error] || ERROR_MESSAGES.default;
  const errorTitle = ERROR_TITLES[error] || ERROR_TITLES.default;
  const closeErrorDialog = () => {
    setAuthErrorOpen(false);
    const params = new URLSearchParams(searchParams?.toString());
    params.delete("error");
    const query = params.toString();
    router.replace(query ? `/login?${query}` : "/login", { scroll: false });
  };
  return (
    <ConfirmDialog
      open={authErrorOpen}
      onClose={closeErrorDialog}
      onConfirm={closeErrorDialog}
      title={errorTitle}
      body={errorMessage}
      confirmButtonText="Okay"
      showIgnoreButton={false}
      showCloseIcon={false}
      modalStyle="toast"
      confirmButtonVariant="text"
    />
  );
};

const LinkedInAuthButton = ({
  isLoading,
  onLogin,
  isMobile,
  label = "Sign in with LinkedIn",
  lastUsed = false,
}) => {
  const theme = useTheme();

  const buttonStyles = {
    px: isMobile ? 1.5 : 1.5,
    pl: isMobile ? 3 : 1.5,
    typography: "button",
    fontSize: "0.875rem",
    lineHeight: 1.5,
    backgroundColor: "#0A66C2",
    color: "white",
    borderRadius: isMobile ? "7px!important" : "50px",
    width: "100%",
    height: { xs: 56, sm: 56 },
    transition: isMobile
      ? "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      : "background-color 0.3s ease",
    "&:hover": {
      backgroundColor: "#004182",
      color: "white",
      // ...(isMobile && {
      //   transform: "translateY(-2px)",
      //   boxShadow: "0 12px 40px rgba(10, 102, 194, 0.4)",
      // }),
    },
    "&:active": {
      color: "white",
      ...(isMobile && { transform: "translateY(0px)" }),
    },
    "&:focus": {
      color: "white",
    },
    "&:disabled": {
      color: "white",
    },
    // ...(isMobile && {
    //   boxShadow: "0 8px 32px rgba(10, 102, 194, 0.3)",
    // }),
  };

  return (
    <Button
      variant="contained"
      size="medium"
      sx={{ ...buttonStyles, position: "relative", overflow: "visible" }}
      onClick={onLogin}
      disabled={isLoading}
    >
      {lastUsed && (
        <Chip
          label="Last used"
          size="small"
          sx={{
            position: "absolute",
            top: -11,
            right: 8,
            height: 20,
            fontSize: "0.68rem",
            zIndex: 2,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
          }}
        />
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isMobile ? "flex-start" : "center",
          gap: 1.5,
          width: "100%",
          color: "inherit",
        }}
      >
        {isLoading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <FaLinkedinIn size={20} />
        )}
        <Box component="span" sx={{ color: "inherit", whiteSpace: "nowrap" }}>
          {label}
        </Box>
      </Box>
    </Button>
  );
};

const GoogleAuthButton = ({
  isLoading,
  onLogin,
  isMobile,
  lastUsed = false,
}) => {
  const theme = useTheme();

  const buttonStyles = {
    px: isMobile ? 1.5 : 1.5,
    pl: isMobile ? 3 : 1.5,
    typography: "button",
    fontSize: "0.875rem",
    lineHeight: 1.5,
    backgroundColor: "background.paper",
    color: "text.primary",
    border: "1.5px solid",
    borderColor: "divider",
    borderRadius: isMobile ? "7px!important" : "50px",
    width: "100%",
    height: { xs: 56, sm: 56 },
    transition: "background-color 0.3s ease, border-color 0.3s ease",
    "&:hover": {
      backgroundColor: "action.hover",
      borderColor: "grey.350",
      color: "text.primary",
    },
    "&:disabled": { opacity: 0.7 },
  };

  return (
    <Button
      variant="outlined"
      size="medium"
      sx={{ ...buttonStyles, position: "relative", overflow: "visible" }}
      onClick={onLogin}
      disabled={isLoading}
    >
      {lastUsed && (
        <Chip
          label="Last used"
          size="small"
          sx={{
            position: "absolute",
            top: -11,
            right: 8,
            height: 20,
            fontSize: "0.68rem",
            zIndex: 2,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
          }}
        />
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isMobile ? "flex-start" : "center",
          gap: 1,
          width: "100%",
        }}
      >
        {isLoading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <FcGoogle size={20} />
        )}
        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
          Sign in with Google
        </Box>
      </Box>
    </Button>
  );
};

const AuthDivider = () => {
  const theme = useTheme();
  const greyColor = theme.palette.grey?.[350] || theme.palette.grey[500];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: {
          xs: "100%",
          sm: "100%",
          md: "320px",
          lg: "320px",
          xl: "320px",
        },
        my: 2,
      }}
    >
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "divider" }} />
      <Typography
        variant="body2"
        sx={{ mx: 2, color: greyColor, fontWeight: 500 }}
      >
        or
      </Typography>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "divider" }} />
    </Box>
  );
};

// Full-page spinner shown while a magic-link sign-in is being processed.
const MagicLinkLoadingScreen = () => (
  <Box
    sx={{
      position: "fixed",
      inset: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2.5,
      backgroundColor: "background.default",
      zIndex: 9999,
    }}
  >
    <Logo width={60} height={60} />
    <CircularProgress size={22} thickness={3} />
    <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5 }}>
      Signing you in&hellip;
    </Typography>
  </Box>
);

const PlatformStats = ({ stats, isMobile }) => {
  const theme = useTheme();
  const greyColor = theme.palette.grey?.[350] || theme.palette.grey[500];

  const count = formatStatNumber(stats?.portfolios);

  if (!count) return null;

  return (
    <Fade in timeout={600}>
      <Typography
        variant="body2"
        sx={{
          color: greyColor,
          mt: isMobile ? 1.5 : 2,
          minHeight: isMobile ? 20 : 24,
          whiteSpace: "nowrap",
          display: "block",
          opacity: count ? 1 : 0.15,
          transform: count ? "translateY(0)" : "translateY(4px)",
          transition: "opacity 0.28s ease, transform 0.28s ease",
          willChange: "opacity, transform",
        }}
      >
        {count ? `Join ${count} people on Meex` : "Join 10,000+ people on Meex"}
      </Typography>
    </Fade>
  );
};

const SignInHeader = ({ isMobile, stats, isLinkingMode }) => {
  const count = formatStatNumber(stats?.portfolios);

  return (
    <Box
      sx={{
        textAlign: "left",
      }}
    >
      <div style={{ pointerEvents: "none" }}>
        <Logo width={70} height={70} />
      </div>
      <Typography
        variant="h1"
        sx={{
          mt: isMobile ? 2 : 4,
          fontSize: { xs: "1.75rem", md: "2rem", lg: "2.5rem" },
        }}
      >
        {isLinkingMode ? "You\u2019re almost in" : "Get Started"}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          mt: 1,
        }}
      >
        {isLinkingMode
          ? "One sign-in with LinkedIn. That\u2019s it."
          : count
          ? `Join ${count} professionals on Meex`
          : "Access Your Meex Hub"}
      </Typography>
    </Box>
  );
};

const SignInDescription = ({ isMobile, isLinkingMode }) => (
  <Fade in timeout={800}>
    {isLinkingMode ? (
      <Typography
        variant="body1"
        sx={{
          mt: isMobile ? 4 : { xs: 10, md: 16 },
          mb: isMobile ? 4 : 5,
          textAlign: "left",
          lineHeight: isMobile ? 1.4 : 1.6,
        }}
      >
        After signing in, you&apos;ll be asked to confirm your professional
        profile with LinkedIn. This is required once, never again.
      </Typography>
    ) : (
      <Typography
        variant="body1"
        sx={{
          mt: isMobile ? 4 : { xs: 10, md: 16 },
          mb: isMobile ? 4 : 5,
          textAlign: "left",
          lineHeight: isMobile ? 1.4 : 1.5,
        }}
      >
        Meex helps you meet anyone, anywhere. Search beyond your network,
        schedule, and meet, all in one place.
      </Typography>
    )}
  </Fade>
);

const PermissionChecklist = ({ isMobile, isLinkingMode, prefixText }) => {
  const theme = useTheme();
  const greyColor = theme.palette.grey?.[350] || theme.palette.grey[500];

  if (isMobile) {
    return (
      <Fade in timeout={1000}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.8,
            mt: 3,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: greyColor, lineHeight: 1.4, fontSize: "0.82rem" }}
          >
            <a
              href="https://meex.co/terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              Terms of Service
            </a>
            {" · "}
            <a
              href="https://meex.co/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              Privacy Policy
            </a>
          </Typography>
        </Box>
      </Fade>
    );
  }

  return (
    <Fade in timeout={1000}>
      <Typography
        variant="body2"
        sx={{
          color: greyColor,
          mt: 0.5,
          lineHeight: 1.6,
          fontSize: "0.88rem",
        }}
      >
        {prefixText && (
          <>
            {prefixText}
            {" · "}
          </>
        )}
        <a
          href="https://meex.co/terms-of-service/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          Terms of Service
        </a>
        {" · "}
        <a
          href="https://meex.co/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          Privacy Policy
        </a>
      </Typography>
    </Fade>
  );
};

const MobileLayout = ({
  isLoading,
  onLogin,
  isGoogleLoading,
  onGoogleLogin,
  stats,
  isLinkingMode,
  lastProvider,
  onEmailLogin,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        px: 2,
        pt: 6.25,
        pb: 3,
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        zIndex: 9999,
      }}
    >
      <Box sx={{ textAlign: "left", width: "94%" }}>
        <Box sx={{ mb: 3 }}>
          <Logo width={70} height={70} />
        </Box>

        <Typography variant="h1" sx={{ mb: 1, fontSize: "1.9rem" }}>
          {isLinkingMode ? "You\u2019re almost in" : "Get Started"}
        </Typography>

        {isLinkingMode ? (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 1, mb: 0 }}
          >
            One sign-in with LinkedIn. That’s it.
          </Typography>
        ) : (
          <PlatformStats stats={stats} isMobile={true} />
        )}

        {isLinkingMode ? (
          <Typography variant="body1" sx={{ mt: 4, mb: 4, lineHeight: 1.6 }}>
            After signing in, you&apos;ll be asked to confirm your professional
            profile with LinkedIn. This is required once, never again.
          </Typography>
        ) : (
          <Typography variant="body1" sx={{ mb: 4, mt: 4, lineHeight: 1.5 }}>
            Meex helps you meet anyone, anywhere. Search beyond your network,
            schedule, and meet, all in one place.
          </Typography>
        )}

        <LinkedInAuthButton
          isLoading={isLoading}
          onLogin={onLogin}
          isMobile={true}
          label={
            isLinkingMode ? "Continue with LinkedIn" : "Sign in with LinkedIn"
          }
          lastUsed={lastProvider === "linkedin"}
        />

        {/* <Typography
          variant="body2"
          sx={{ color: "text.disabled", mt: 1, fontSize: "0.88rem" }}
        >
          First time only · takes less than a minute
        </Typography> */}

        {!isLinkingMode && (
          <>
            <AuthDivider />

            <GoogleAuthButton
              isLoading={isGoogleLoading}
              onLogin={onGoogleLogin}
              isMobile={true}
              lastUsed={lastProvider === "google"}
            />

            <EmailAuthSection
              disabled={isLoading || isGoogleLoading}
              isMobile={true}
              onSignInStart={onEmailLogin}
              lastUsed={lastProvider === "email"}
            />

            <Typography
              variant="body2"
              sx={{ color: "text.disabled", mt: 0, fontSize: "0.88rem" }}
            >
              If you sign up with Google or email, there is one quick one-time
              LinkedIn step.
            </Typography>
          </>
        )}

        <PermissionChecklist isMobile={true} isLinkingMode={isLinkingMode} />

        <AuthErrorMessage />
      </Box>
      <AppVersionFooter
        sx={{ position: "absolute", bottom: 0, left: 0, right: 0 }}
      />
    </Box>
  );
};

const DesktopLayout = ({
  isLoading,
  onLogin,
  isGoogleLoading,
  onGoogleLogin,
  stats,
  isLinkingMode,
  lastProvider,
  onEmailLogin,
}) => (
  <Grid
    container
    sx={{
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      backgroundColor: "background.dark",
      position: "relative",
    }}
  >
    <Grid
      size={{ xs: 12, sm: 12, md: 11, lg: 11, xl: 7 }}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <DashboardCard
        borderRadius="16px"
        titleVariant="h1b"
        sx={{
          p: 5,
          border: "0.5px solid",
          borderColor: "grey.200",
          backgroundColor: "background.paper",
          boxShadow: "none",
        }}
      >
        <Grid container>
          <Grid
            size={{ xs: 12, sm: 12, md: 12, lg: 5, xl: 5 }}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              flexDirection: "column",
            }}
          >
            <SignInHeader
              isMobile={false}
              stats={stats}
              isLinkingMode={isLinkingMode}
            />
          </Grid>

          <Grid
            size={{ xs: 12, sm: 12, md: 12, lg: 7, xl: 7 }}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              flexDirection: "column",
            }}
          >
            <SignInDescription isMobile={false} isLinkingMode={isLinkingMode} />

            <Box
              sx={{ width: "100%", display: "flex", flexDirection: "column" }}
            >
              {isLinkingMode ? (
                // Linking mode: half-width button with minimum to avoid text wrap
                <Box sx={{ width: "50%", minWidth: 260 }}>
                  <LinkedInAuthButton
                    isLoading={isLoading}
                    onLogin={onLogin}
                    isMobile={false}
                    lastUsed={lastProvider === "linkedin"}
                    label="Continue with LinkedIn"
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "stretch",
                    width: "100%",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <LinkedInAuthButton
                      isLoading={isLoading}
                      onLogin={onLogin}
                      isMobile={false}
                      lastUsed={lastProvider === "linkedin"}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      px: 1.5,
                    }}
                  >
                    <Box
                      sx={{ flex: 1, width: "1px", backgroundColor: "divider" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.disabled",
                        fontWeight: 500,
                        py: 0.5,
                        fontSize: "0.82rem",
                      }}
                    >
                      or
                    </Typography>
                    <Box
                      sx={{ flex: 1, width: "1px", backgroundColor: "divider" }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <GoogleAuthButton
                      isLoading={isGoogleLoading}
                      onLogin={onGoogleLogin}
                      isMobile={false}
                      lastUsed={lastProvider === "google"}
                    />
                  </Box>
                </Box>
              )}
              {isLinkingMode && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.disabled",
                    mt: 1.5,
                    fontSize: "0.88rem",
                  }}
                >
                  One-time step &middot; takes less than a minute
                </Typography>
              )}

              {!isLinkingMode && (
                <Box sx={{ width: "60%", ml: "auto" }}>
                  <EmailAuthSection
                    disabled={isLoading || isGoogleLoading}
                    onSignInStart={onEmailLogin}
                    lastUsed={lastProvider === "email"}
                  />
                </Box>
              )}
            </Box>

            <PermissionChecklist
              isMobile={false}
              isLinkingMode={isLinkingMode}
              prefixText={
                isLinkingMode
                  ? undefined
                  : "LinkedIn signs you in instantly \u00b7 Using Google or email? You’ll complete one quick LinkedIn step."
              }
            />

            <AuthErrorMessage />
          </Grid>
        </Grid>
      </DashboardCard>
    </Grid>
    <AppVersionFooter
      sx={{ position: "absolute", bottom: 0, left: 0, right: 0 }}
    />
  </Grid>
);

export default function Login() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const step = searchParams?.get("step");
  const isLinkingMode = step === "link-linkedin";

  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isMagicSignInLoading, setIsMagicSignInLoading] = useState(false);
  const [platformStats, setPlatformStats] = useState(null);
  const {
    lastProvider: displayedLastProvider,
    rememberProvider,
  } = useLastLoginProvider();
  const hasRequested = useRef(false);
  const hasGoogleRequested = useRef(false);
  const hasMagicSignInRef = useRef(false);

  useEffect(() => {
    // Redirect once the user has a real LinkedIn sub (fully authenticated).
    // Using session.user.sub instead of !isLinkingMode so we also redirect
    // after the Google→LinkedIn link flow completes (sub was empty during
    // linking, becomes the LinkedIn sub once the link is done).
    if (status === "authenticated" && session?.user?.sub) {
      const paramCb = searchParams?.get("callbackUrl");
      const pendingParam = searchParams?.get("pending");
      const dest = paramCb && paramCb.startsWith("/") ? paramCb : "/";

      if (pendingParam) {
        // Safety-net: if the NextAuth cookie was lost during the LinkedIn
        // OAuth redirect, wasLinkingGoogle won't have run. Call the
        // complete-google-link endpoint to finish the link now.
        // It's idempotent — safe to call even if wasLinkingGoogle already ran.
        fetch("/api/user/complete-google-link", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pending: pendingParam }),
          credentials: "include",
        }).finally(() => router.push(dest));
      } else {
        router.push(dest);
      }
    }
  }, [status, session?.user?.sub, router, searchParams]);

  // Magic-link auto-sign-in: when the user clicks the emailed link they land on
  // /login?magic_token=UUID. Detect it and call signIn immediately.
  useEffect(() => {
    const magicToken = searchParams?.get("magic_token");
    if (
      !magicToken ||
      status !== "unauthenticated" ||
      hasMagicSignInRef.current
    )
      return;
    hasMagicSignInRef.current = true;
    setIsMagicSignInLoading(true);
    const paramCb = searchParams?.get("callbackUrl");
    const callbackUrl = paramCb && paramCb.startsWith("/") ? paramCb : "/";
    track(EVENTS.SIGNUP_STARTED, {
      provider: "magic-email",
      trigger: "magic_link_clicked",
      callback_url: callbackUrl,
      entry_url:
        typeof window !== "undefined" ? window.location.href : undefined,
      entry_pathname:
        typeof window !== "undefined" ? window.location.pathname : undefined,
      entry_referrer:
        typeof document !== "undefined" ? document.referrer : undefined,
    });
    signIn("magic-email", { token: magicToken, callbackUrl }).catch(() => {
      // signIn() rejects on network error; NextAuth errors redirect back here
      // with ?error=CredentialsSignin so AuthErrorMessage handles them.
      track(EVENTS.LOGIN_FAILED, {
        provider: "magic-email",
        reason: "signIn_rejected",
      });
      setIsMagicSignInLoading(false);
      hasMagicSignInRef.current = false;
    });
  }, [status, searchParams]);

  useEffect(() => {
    fetch("/api/stats/platform")
      .then((res) => res.json())
      .then((data) => setPlatformStats(data))
      .catch(() => {});
  }, []);

  const handleLogin = useCallback(async () => {
    if (hasRequested.current || isButtonLoading) return;

    hasRequested.current = true;
    setIsButtonLoading(true);
    rememberProvider("linkedin");

    try {
      // If NextAuth redirected here with a callbackUrl query param (e.g. from
      // /s/:requestId), honour that URL so the user lands on the right page
      // after OAuth. Validate it starts with "/" to prevent open-redirect.
      const paramCb = searchParams?.get("callbackUrl");
      const pendingParam = searchParams?.get("pending");
      // When a pending Google link is in progress, redirect to /link-complete
      // (not /login) after LinkedIn OAuth. NextAuth refuses to redirect back
      // to the sign-in page (/login) to prevent loops — /link-complete is a
      // neutral page that calls complete-google-link then forwards to dest.
      const callbackUrl =
        isLinkingMode && pendingParam
          ? `/link-complete?pending=${encodeURIComponent(pendingParam)}${
              paramCb && paramCb.startsWith("/")
                ? `&callbackUrl=${encodeURIComponent(paramCb)}`
                : ""
            }`
          : paramCb && paramCb.startsWith("/")
          ? paramCb
          : window.location.href;
      track(EVENTS.SIGNUP_STARTED, {
        provider: "linkedin",
        is_linking_mode: Boolean(isLinkingMode),
        callback_url: callbackUrl,
        entry_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        entry_pathname:
          typeof window !== "undefined" ? window.location.pathname : undefined,
        entry_referrer:
          typeof document !== "undefined" ? document.referrer : undefined,
      });
      await signIn("linkedin", {
        callbackUrl,
      });
    } catch (error) {
      console.error("Login failed:", error);
      track(EVENTS.LOGIN_FAILED, {
        provider: "linkedin",
        reason: error?.message?.slice(0, 200) || "unknown",
      });
      hasRequested.current = false;
      setIsButtonLoading(false);
    }
  }, [isButtonLoading, rememberProvider]);

  const handleGoogleLogin = useCallback(async () => {
    if (hasGoogleRequested.current || isGoogleLoading) return;

    hasGoogleRequested.current = true;
    setIsGoogleLoading(true);
    rememberProvider("google");

    try {
      const paramCb = searchParams?.get("callbackUrl");
      const callbackUrl =
        paramCb && paramCb.startsWith("/") ? paramCb : window.location.href;
      track(EVENTS.SIGNUP_STARTED, {
        provider: "google",
        callback_url: callbackUrl,
        entry_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        entry_pathname:
          typeof window !== "undefined" ? window.location.pathname : undefined,
        entry_referrer:
          typeof document !== "undefined" ? document.referrer : undefined,
      });
      await signIn("google", { callbackUrl });
    } catch (error) {
      console.error("Google login failed:", error);
      track(EVENTS.LOGIN_FAILED, {
        provider: "google",
        reason: error?.message?.slice(0, 200) || "unknown",
      });
      hasGoogleRequested.current = false;
      setIsGoogleLoading(false);
    }
  }, [isGoogleLoading, rememberProvider]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleLogin();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleLogin]);

  const layoutProps = {
    isLoading: isButtonLoading,
    onLogin: handleLogin,
    isGoogleLoading,
    onGoogleLogin: handleGoogleLogin,
    stats: platformStats,
    isLinkingMode,
    lastProvider: displayedLastProvider,
    onEmailLogin: () => rememberProvider("email"),
  };

  // Don't render login UI while session is resolving or an authenticated
  // user is being redirected — prevents the brief login-page flash.
  // Exception: linking mode must always render so the user can complete
  // the LinkedIn verification step.
  if (status === "loading") return null;
  // Magic-link sign-in in progress — show spinner instead of blank page.
  if (isMagicSignInLoading) return <MagicLinkLoadingScreen />;
  if (status === "authenticated" && session?.user?.sub) return null;

  return isMobile ? (
    <MobileLayout {...layoutProps} />
  ) : (
    <PageContainer title="Meex" description="Sign in to Meex">
      <DesktopLayout {...layoutProps} />
    </PageContainer>
  );
}
